import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TemplateBuilder from '../components/TemplateBuilder';
import Handlebars from 'handlebars';

export default function Demo() {
  const navigate = useNavigate();
  const [generatedHtml, setGeneratedHtml] = useState('');

  // Dummy user data for preview
  const dummyUserData = {
    displayName: 'John Doe',
    jobTitle: 'Senior Consultant',
    companyName: 'Virtue ApS',
    email: 'john.doe@virtue.dk',
    mobilePhone: '+45 12 34 56 78',
    businessPhones: '+45 12 34 56 78',
    website: 'www.virtue.dk',
    department: 'Consulting',
    officeLocation: 'Copenhagen',
    logo: '/virtue-logo.png',
    logo1: '/virtue-logo.png',
  };

  // Render preview with dummy data
  const renderPreview = () => {
    try {
      const template = Handlebars.compile(generatedHtml);
      return template(dummyUserData);
    } catch (error) {
      return generatedHtml; // Return raw HTML if compilation fails
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', padding: '20px' }}>
      {/* Demo Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '20px 30px',
        borderRadius: '12px',
        marginBottom: '20px',
        textAlign: 'center',
        boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
      }}>
        <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700' }}>
          🎨 VirtueSign Template Builder Demo
        </h1>
        <p style={{ margin: '10px 0 0', fontSize: '16px', opacity: 0.95 }}>
          Try the actual template builder with sample data - no login required!
        </p>
        <p style={{ margin: '10px 0 0', fontSize: '14px', opacity: 0.85 }}>
          (Demo mode: changes won't be saved)
        </p>
      </div>

      {/* Main Content */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px',
        maxWidth: '1600px',
        margin: '0 auto',
      }}>
        {/* Left: Template Builder */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '30px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <div style={{ marginBottom: '20px' }}>
            <h2 style={{ margin: 0, fontSize: '20px', color: '#667eea' }}>
              Template Builder
            </h2>
            <p style={{ margin: '5px 0 0', fontSize: '14px', color: '#6b7280' }}>
              Drag fields to customize the signature layout
            </p>
          </div>
          
          <TemplateBuilder
            onGenerate={setGeneratedHtml}
            demoMode={true}
            initialData={{
              layout: 'horizontal',
              layoutMode: 'list',
              gridRows: 3,
              gridCols: 2,
              logoUrl: '/virtue-logo.png',
              logoWidth: '100',
              logoPosition: 'left',
              logoAlignToField: 'name',
              secondaryLogoUrl: '',
              secondaryLogoWidth: '80',
              secondaryLogoPosition: 'bottom',
              showSecondaryLogo: false,
              companyName: 'Virtue ApS',
              website: 'https://www.virtue.dk',
              vatNumber: '',
              addressLine1: '',
              addressLine2: '',
              socialLinks: {
                linkedin: '',
                twitter: '',
                facebook: '',
              },
              fontFamily: 'Arial, sans-serif',
              fontSize: '14',
              primaryColor: '#667eea',
              showSocialIcons: false,
              fields: [
                { id: 'logo', label: 'Company Logo', variable: 'logo', enabled: true },
                { id: 'name', label: 'Full Name', variable: 'displayName', enabled: true },
                { id: 'jobTitle', label: 'Job Title', variable: 'jobTitle', enabled: true, conditional: true },
                { id: 'company', label: 'Company', variable: 'companyName', enabled: true },
                { id: 'email', label: 'Email', variable: 'email', enabled: true, prefix: 'E:' },
                { id: 'mobile', label: 'Mobile Phone', variable: 'mobilePhone', enabled: true, conditional: true, prefix: 'M:' },
                { id: 'website', label: 'Website', variable: 'website', enabled: true },
              ],
              customTexts: {},
            }}
          />
        </div>

        {/* Right: Live Preview */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '30px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          position: 'sticky',
          top: '20px',
          height: 'fit-content',
        }}>
          <div style={{ marginBottom: '20px' }}>
            <h2 style={{ margin: 0, fontSize: '20px', color: '#667eea' }}>
              Live Preview
            </h2>
            <p style={{ margin: '5px 0 0', fontSize: '14px', color: '#6b7280' }}>
              See how the signature looks in real-time
            </p>
          </div>

          <div style={{
            border: '2px solid #e5e7eb',
            borderRadius: '8px',
            padding: '25px',
            background: '#fafafa',
            minHeight: '300px',
          }}>
            {generatedHtml ? (
              <div dangerouslySetInnerHTML={{ __html: renderPreview() }} />
            ) : (
              <p style={{ color: '#9ca3af', textAlign: 'center', padding: '50px 20px' }}>
                Signature preview will appear here...
              </p>
            )}
          </div>

          <div style={{
            marginTop: '15px',
            padding: '15px',
            background: '#f0f9ff',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#1e40af',
          }}>
            <strong>Sample Data:</strong> John Doe, Senior Consultant at Virtue ApS
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div style={{
        maxWidth: '800px',
        margin: '40px auto 0',
        textAlign: 'center',
        background: 'white',
        padding: '40px',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
        <h3 style={{ margin: 0, fontSize: '24px', color: '#1f2937' }}>
          Ready to create signatures for your team?
        </h3>
        <p style={{ margin: '15px 0', fontSize: '16px', color: '#6b7280' }}>
          Start your 14-day free trial and deploy professional email signatures in minutes
        </p>
        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', marginTop: '25px' }}>
          <button
            onClick={() => navigate('/login')}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '15px 40px',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '16px',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
              transition: 'transform 0.2s',
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            Start Free Trial
          </button>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'transparent',
              color: '#667eea',
              padding: '15px 40px',
              border: '2px solid #667eea',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '16px',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#f0f9ff';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}
